import React from 'react';
import styled from 'styled-components';

import DebriefDialogTitle from 'ff/debrief/components/modals/DebriefDialogTitle';

import { useDebriefModal } from 'ff/debrief/modalManager';
import { useCurrentTracklog } from 'ff/debrief/hooks';
import ConnectLogbookInfo from '../logbookAssociation/ConnectLogbookInfo';
import ConnectLogbookForm from '../logbookAssociation/ConnectLogbookForm';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import { LogbookEntry } from 'shared/sharedTypes/logbook/LogbookEntryModels';
import { LogbookAircraft } from 'shared/sharedTypes/logbook/LogbookAircraftModels';
import { useCurrentLogbook } from 'ff/debrief/hooks/useCurrentLogbook';
import {
    useAddLogbookObjectIdMutation,
    useDeleteLogbookEntryAssociationMutation,
} from 'ff/debrief/features/TracklogApi';
import { TracklogLogbookAssociation } from 'ff/debrief/models';

const LinkLogbookModal: React.FC = () => {
    const { isOpen, close } = useDebriefModal('LinkLogbook');
    const { tracklog } = useCurrentTracklog();
    const {
        isLogbookEntriesError,
        addLogbookEntry,
        isEntryLoading,
        isEntrySuccess,
        entryReset,
    } = useCurrentLogbook();

    const [deleteLogbookEntryAssociation] =
        useDeleteLogbookEntryAssociationMutation();
    const [addLogbookObjectId] = useAddLogbookObjectIdMutation();

    const aircraft = {
        accountUuid: tracklog.accountUuid,
        tailNumber: tracklog.tailNumber,
    } as LogbookAircraft;

    const handleClose = () => {
        close();
        entryReset();
    };

    const handleClearAllAssociations = () => {
        if (tracklog.logbookAssociationDetails.length === 0) {
            return;
        }
        tracklog.logbookAssociationDetails.forEach(association => {
            const associationLog: TracklogLogbookAssociation = {
                tracklogObjectId: tracklog.objectId,
                logbookEntryObjectId: association.logbookEntryID,
            };
            deleteLogbookEntryAssociation(associationLog);
        });
    };

    const createLogEntryHandler = async () => {
        const logbookEntryData: Partial<LogbookEntry> = {
            accountUuid: tracklog.accountUuid,
            provisional: 1,
            departureAirport: tracklog.derivedOrigin,
            destinationAirport: tracklog.derivedDestination,
            aircraft: aircraft,
            sourceDescription: tracklog.name,
            source: 'FFMTracklog',
            sourceId: tracklog.objectId,
            flightDate: tracklog.timestampStart?.split('T')[0],
            totalTimeDuration: tracklog.totalDurationSeconds,
        };

        try {
            const result = await addLogbookEntry(logbookEntryData);
            if (result) {
                const association: TracklogLogbookAssociation = {
                    tracklogObjectId: tracklog.objectId,
                    logbookEntryObjectId: result.data.result.responseObject,
                };
                addLogbookObjectId(association);
                window.open(
                    `/logbook#/drafts?entryId=${result.data.result.responseObject}`,
                    '_blank'
                );
            }
        } catch (error) {
            console.error('Failed to create logbook entry:', error);
        }
    };

    const circleProgress = (
        <CircularProgress size={21} sx={{ color: '#fffff' }} />
    );

    const createEntryContent = (() => {
        if (isEntryLoading) return circleProgress;
        if (isEntrySuccess) return 'Created New Entry In Drafts';
        return 'Create New Entry';
    })();

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            sx={{
                width: '450px',
                maxWidth: 'none',
                margin: 'auto',
            }}
        >
            <DebriefDialogTitle onClose={handleClose}>
                Add to Logbook
            </DebriefDialogTitle>
            <LinkLogDialogContent>
                <ConnectLogbookInfo />
                <Stack sx={{ gap: '0px', justifyContent: 'center' }}>
                    {isLogbookEntriesError ? (
                        <Typography>Error loading logbooks</Typography>
                    ) : (
                        <List
                            sx={{
                                width: '100%',
                                maxWidth: 380,
                                bgcolor: '#ffffff',
                                position: 'relative',
                                overflow: 'auto',
                                maxHeight: 300,
                                borderRadius: '9px 9px 0 0',
                                '& ul': { padding: 0 },
                            }}
                            subheader={<li />}
                        >
                            <ConnectLogbookForm />
                        </List>
                    )}
                    <Divider
                        sx={{
                            position: 'sticky',
                            borderColor: '#EFF1F3',
                            borderBottomWidth: '3px',
                        }}
                    />
                </Stack>
                <Stack alignItems="center" gap="15px" marginTop="4px">
                    <Button
                        variant="outlined"
                        onClick={createLogEntryHandler}
                        disabled={isEntryLoading}
                        disableRipple={false}
                        sx={{
                            borderColor: '#008CC4',
                            borderWidth: '2px',
                            color: '#008CC4',
                            width: '250px',
                            ':hover': {
                                borderWidth: '2px',
                            },
                        }}
                    >
                        {createEntryContent}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleClearAllAssociations}
                        disableRipple={false}
                        sx={{
                            borderColor: '#ff6e6e',
                            borderWidth: '2px',
                            color: '#ff6e6e',
                            width: '250px',
                            ':hover': {
                                borderWidth: '2px',
                            },
                        }}
                    >
                        Clear All Associations
                    </Button>
                </Stack>
            </LinkLogDialogContent>
        </Dialog>
    );
};

const LinkLogDialogContent = styled(DialogContent)`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export default LinkLogbookModal;
